import React, { useState, useEffect } from 'react';
import { useNavigate, Routes, Route, useParams, useLocation, Navigate } from 'react-router-dom';
import { Card, Table, Btn, RNLLoader, Modal, Input, Select, Textarea, FormGrid, Badge, HeaderBack } from '../components/UI';
import * as api from '../api';
import BOQFormPage from './BOQFormPage';
import { BOQItemsTab } from './BOQPage';
import BOQImportView from '../components/boq/BOQImportView';
import { hasRole } from '../utils';
import ModuleCard from '../components/ModuleCard';
import MaterialMappingPage from './MaterialMappingPage';
import CodeGeneratorPage from './CodeGeneratorPage';
import { BOQ_BASE } from '../components/boq/paths';

// BOQ Hub's sub-modules, shown as cards on /boq-hub
const HUB_CARDS = [
  { id: 'budget-master', label: 'Budget Master', description: 'Project packages, revisions and BOQs — create, import and price bills of quantities.' },
  { id: 'material-mappings', label: 'Material Mappings', description: 'Map each BOQ line to the stock materials that execute it — then trace requests, POs and site use back to the BOQ.' },
  { id: 'code-generator', label: 'Code Generator', description: 'Issue BOQ codes in the MEP format (RDV-ELECT-0001) and sub-codes, keep the register, and re-code BOQ lines.' },
];

// who may change BOQs (the backend's BOQ write routes allow the same roles)
const BOQ_WRITERS = ['project_manager', 'quantity_surveyor'];

// Back now lives in the top bar; this just declares the view's Back action.
const PillBackButton = ({ onClick, children }) => (
  <HeaderBack onClick={onClick}>{children}</HeaderBack>
);

// Downloads an empty BOQ workbook in the layout Import BOQ (Excel) reads, with a sheet per building of the project.
function DownloadTemplateBtn({ projectId, projectName, toast }) {
  const [busy, setBusy] = useState(false);
  const download = async () => {
    setBusy(true);
    try {
      const file = await api.downloadBoqTemplate(projectId);
      const url = URL.createObjectURL(file);
      const a = document.createElement('a');
      a.href = url;
      a.download = `BOQ-template${projectName ? `-${projectName.replace(/[^A-Za-z0-9._-]+/g, '-')}` : ''}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      toast?.show?.(e.message || 'Could not download the template', 'error');
    }
    setBusy(false);
  };
  return <Btn variant="secondary" onClick={download} loading={busy} title="An empty BOQ workbook in the format Import BOQ (Excel) reads">Download Template</Btn>;
}

// ─────────────────────────────────────────────
// Project Summary View
// ─────────────────────────────────────────────
function ProjectSummaryView({ projects }) {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const selectedProject = projects.find(p => p.project_id === projectId);

  if (!selectedProject) return null;

  return (
    <div className="fade-in" style={{ padding: '40px 24px', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <div
        className="division-card"
        onClick={() => navigate(`${BOQ_BASE}/project/${selectedProject.project_id}/packages`)}
        style={{ maxWidth: 600, width: '100%', margin: '0 auto', textAlign: 'left' }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div className="icon-wrapper">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 20h16a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.93a2 2 0 0 1-1.66-.9l-.82-1.2A2 2 0 0 0 7.93 3H4a2 2 0 0 0-2 2v13c0 1.1.9 2 2 2Z" /></svg>
          </div>
          <span style={{
            background: selectedProject.status === 'active' ? '#10b981' : '#cbd5e1',
            border: '2px solid black',
            borderRadius: 6,
            padding: '8px 18px',
            fontSize: 15,
            fontWeight: 800,
            color: 'black',
            textTransform: 'uppercase',
            letterSpacing: '0.05em'
          }}>
            {selectedProject.status}
          </span>
        </div>

        <div>
          <h3 style={{ fontSize: 24, fontWeight: 800, color: '#0f172a', margin: '16px 0 8px 0' }}>{selectedProject.project_title || selectedProject.project_name}</h3>
          <div style={{
            display: 'inline-block',
            marginTop: 4,
            padding: '4px 10px',
            background: 'rgba(11, 49, 139, 0.08)',
            border: '2px solid #0b318bff',
            borderRadius: 6,
            color: '#0b318bff',
            fontWeight: 800,
            fontSize: 13,
            letterSpacing: '0.05em'
          }}>
            {selectedProject.project_code}
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24, marginTop: 16 }}>
          <div>
            <div style={{ fontSize: 12, fontWeight: 800, color: '#475569', letterSpacing: '0.05em', marginBottom: 6 }}>CLIENT NAME</div>
            <div style={{ fontSize: 15, fontWeight: 700, color: '#0f172a' }}>{selectedProject.client_name || '—'}</div>
          </div>
          <div>
            <div style={{ fontSize: 12, fontWeight: 800, color: '#475569', letterSpacing: '0.05em', marginBottom: 6 }}>LOCATION</div>
            <div style={{ fontSize: 15, fontWeight: 700, color: '#0f172a' }}>{selectedProject.location || '—'}</div>
          </div>
        </div>

        <div style={{ flex: 1 }} />
        <div style={{ borderTop: '4px dashed #3174ccff', margin: '16px 0' }} />

        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            background: '#0b318bff',
            color: '#ffffff',
            fontWeight: 700,
            fontSize: 13,
            padding: '10px 20px',
            borderRadius: 8,
            boxShadow: '0 4px 6px -1px rgba(11, 49, 139, 0.2)'
          }}>
            <span>Open Client - Packages</span>
            <svg className="arrow-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14"></path><path d="m12 5 7 7-7 7"></path></svg>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// Packages View
// ─────────────────────────────────────────────
function PackagesView({ toast, projects = [], user }) {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [packages, setPackages] = useState([]);
  const [packageTypes, setPackageTypes] = useState([]);
  const [loading, setLoading] = useState(true);

  const [showAdd, setShowAdd] = useState(false);
  const [editingPackage, setEditingPackage] = useState(null);
  const [packageToDelete, setPackageToDelete] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState({
    code: '', name: '', description: '', package_type_id: '', status: 'pending'
  });

  const loadData = async () => {
    setLoading(true);
    try {
      const [pkgs, types] = await Promise.all([
        api.listProjectPackages(projectId),
        api.getPackageTypes()
      ]);
      setPackages(pkgs || []);
      setPackageTypes(types || []);
    } catch (e) {
      toast?.show?.('Failed to load packages', 'error');
    }
    setLoading(false);
  };

  useEffect(() => { loadData(); }, [projectId]);

  const handleAddOrUpdate = async () => {
    if (!formData.code || !formData.name || !formData.package_type_id) {
      toast?.show?.('Please fill required fields (Code, Name, Type)', 'error');
      return;
    }
    setIsSaving(true);
    try {
      if (editingPackage) {
        await api.updateProjectPackage(editingPackage.id, formData);
        toast?.show?.('Package updated successfully', 'success');
      } else {
        await api.createProjectPackage(projectId, formData);
        toast?.show?.('Package created successfully', 'success');
      }
      setShowAdd(false);
      setEditingPackage(null);
      setFormData({ code: '', name: '', description: '', package_type_id: '', status: 'pending' });
      loadData();
    } catch (e) {
      toast?.show?.(e.message || 'Failed to create package', 'error');
    }
    setIsSaving(false);
  };

  const handleDeletePackage = async () => {
    try {
      await api.deleteProjectPackage(packageToDelete.id);
      toast?.show?.('Package deleted successfully', 'success');
      setPackageToDelete(null);
      loadData();
    } catch (e) {
      toast?.show?.(e.message || 'Failed to delete package', 'error');
    }
  };

  if (loading) return <RNLLoader title="Loading Packages..." />;

  // Group by package type
  const grouped = {};
  packages.forEach(p => {
    const typeName = p.package_type?.name || 'Unknown Type';
    if (!grouped[typeName]) grouped[typeName] = [];
    grouped[typeName].push(p);
  });

  const project = projects.find(p => p.project_id === projectId);
  const displayTitle = project ? project.project_name : 'BOQ Packages';

  return (
    <div className="fade-in">
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
        <PillBackButton onClick={() => navigate(`${BOQ_BASE}/project/${projectId}`)}>Back to Client</PillBackButton>
        <h2 style={{ margin: 0, fontSize: 24, fontWeight: 800, color: '#0f172a' }}>{displayTitle}</h2>
        <div style={{ flex: 1 }} />
        {hasRole(user, BOQ_WRITERS) && <DownloadTemplateBtn projectId={projectId} projectName={project?.project_name} toast={toast} />}
        {hasRole(user, BOQ_WRITERS) && <Btn variant="secondary" onClick={() => navigate(`${BOQ_BASE}/project/${projectId}/import`)}>Import BOQ (Excel)</Btn>}
        <Btn onClick={() => setShowAdd(true)}>New Package</Btn>
      </div>

      {Object.keys(grouped).length === 0 ? (
        <Card style={{ padding: 60, textAlign: 'center' }}>
          <h3 style={{ fontSize: 18, color: '#64748b' }}>No packages found for this project</h3>
        </Card>
      ) : (
        Object.keys(grouped).map(type => (
          <div key={type} style={{ marginBottom: 32 }}>
            <h3 style={{ fontSize: 16, fontWeight: 800, color: '#334155', marginBottom: 16, paddingLeft: 8, borderLeft: '4px solid #0b318b' }}>{type}</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
              {grouped[type].map(pkg => (
                <div
                  key={pkg.id}
                  onClick={() => navigate(`${BOQ_BASE}/project/${projectId}/packages/${pkg.id}/revisions`)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    background: '#e2e8f0',
                    border: '1px solid black',
                    borderRadius: 12,
                    padding: '16px 20px',
                    cursor: 'pointer',
                    gap: 16,
                    boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
                    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                    transform: 'translateY(0)',
                  }}
                  onMouseOver={e => {
                    e.currentTarget.style.borderColor = '#0b318bff';
                    e.currentTarget.style.background = '#cbd5e1';
                    e.currentTarget.style.transform = 'translateY(-4px)';
                  }}
                  onMouseOut={e => {
                    e.currentTarget.style.borderColor = 'black';
                    e.currentTarget.style.background = '#e2e8f0';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  <div style={{
                    width: 44, height: 44,
                    background: '#f1f5f9',
                    borderRadius: 10,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: '#64748b'
                  }}>
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline><line x1="16" y1="13" x2="8" y2="13"></line><line x1="16" y1="17" x2="8" y2="17"></line><polyline points="10 9 9 9 8 9"></polyline></svg>
                  </div>

                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <span style={{ fontFamily: 'var(--mono)', fontWeight: 800, color: '#0b318bff', fontSize: 13 }}>{pkg.code}</span>
                      <span style={{ fontWeight: 700, color: '#0f172a', fontSize: 15 }}>{pkg.name}</span>
                      <Badge color={pkg.status === 'approved' ? 'green' : pkg.status === 'pending' ? 'amber' : 'gray'}>{pkg.status.toUpperCase()}</Badge>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: 16, fontSize: 12, color: '#64748b' }}>
                      {pkg.created_at && (
                        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect><line x1="16" y1="2" x2="16" y2="6"></line><line x1="8" y1="2" x2="8" y2="6"></line><line x1="3" y1="10" x2="21" y2="10"></line></svg>
                          {pkg.created_at.split('T')[0]}
                        </span>
                      )}
                      {pkg.prepared_by && (
                        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path><circle cx="12" cy="7" r="4"></circle></svg>
                          Prep: {pkg.prepared_by}
                        </span>
                      )}
                      {pkg.ref && (
                        <span>Ref: {pkg.ref}</span>
                      )}
                      {pkg.description && <span style={{ fontStyle: 'italic', color: '#94a3b8' }}>{pkg.description}</span>}
                    </div>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                    <button
                      onClick={(e) => { 
                        e.stopPropagation();
                        setFormData({
                          code: pkg.code,
                          name: pkg.name,
                          description: pkg.description || '',
                          package_type_id: pkg.package_type_id,
                          status: pkg.status
                        });
                        setEditingPackage(pkg);
                        setShowAdd(true);
                      }}
                      style={{
                        background: 'transparent',
                        border: '1.5px solid #f59e0b',
                        borderRadius: 8,
                        cursor: 'pointer',
                        color: '#f59e0b',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        padding: 8,
                        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
                      }}
                      onMouseOver={e => {
                        e.currentTarget.style.background = '#f59e0b';
                        e.currentTarget.style.color = '#ffffff';
                        e.currentTarget.style.boxShadow = '0 0 12px rgba(245, 158, 11, 0.6)';
                      }}
                      onMouseOut={e => {
                        e.currentTarget.style.background = 'transparent';
                        e.currentTarget.style.color = '#f59e0b';
                        e.currentTarget.style.boxShadow = 'none';
                      }}
                      title="Edit Package"
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"></path></svg>
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); setPackageToDelete(pkg); }}
                      style={{
                        background: 'transparent',
                        border: '1.5px solid #ef4444',
                        borderRadius: 8,
                        cursor: 'pointer',
                        color: '#ef4444',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        padding: 8,
                        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
                      }}
                      onMouseOver={e => {
                        e.currentTarget.style.background = '#ef4444';
                        e.currentTarget.style.color = '#ffffff';
                        e.currentTarget.style.boxShadow = '0 0 12px rgba(239, 68, 68, 0.6)';
                      }}
                      onMouseOut={e => {
                        e.currentTarget.style.background = 'transparent';
                        e.currentTarget.style.color = '#ef4444';
                        e.currentTarget.style.boxShadow = 'none';
                      }}
                      title="Delete Package"
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18"></path><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path></svg>
                    </button>

                    <Btn style={{
                      background: '#1e3a8a',
                      color: '#fff',
                      borderRadius: 20,
                      padding: '8px 16px',
                      fontSize: 13,
                      fontWeight: 600,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6
                    }}>
                      Open Package
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="m9 18 6-6-6-6" /></svg>
                    </Btn>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))
      )}

      {showAdd && (
        <Modal title={editingPackage ? "Edit Package" : "Create New Package"} onClose={() => { setShowAdd(false); setEditingPackage(null); setFormData({ code: '', name: '', description: '', package_type_id: '', status: 'pending' }); }} width={600}>
          <FormGrid cols={2}>
            <div>
              <label>Package Code</label>
              <Input value={formData.code} onChange={v => setFormData({ ...formData, code: v })} placeholder="e.g. PKG-01" />
            </div>
            <div>
              <label>Package Name</label>
              <Input value={formData.name} onChange={v => setFormData({ ...formData, name: v })} placeholder="e.g. Original Contract" />
            </div>
            <div style={{ gridColumn: 'span 2' }}>
              <label>Package Type</label>
              <Select
                value={formData.package_type_id}
                onChange={v => setFormData({ ...formData, package_type_id: v })}
                options={[
                  { value: '', label: '-- Select Type --' },
                  ...packageTypes.map(t => ({ value: t.id, label: t.name }))
                ]}
              />
            </div>
            <div style={{ gridColumn: 'span 2' }}>
              <label>Description</label>
              <Textarea value={formData.description} onChange={v => setFormData({ ...formData, description: v })} rows={3} />
            </div>
          </FormGrid>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 24 }}>
            <Btn variant="ghost" onClick={() => setShowAdd(false)}>Cancel</Btn>
            <Btn onClick={handleAddOrUpdate} disabled={isSaving}>{isSaving ? 'Saving...' : (editingPackage ? 'Update Package' : 'Save Package')}</Btn>
          </div>
        </Modal>
      )}

      {packageToDelete && (
        <Modal title="Delete Package" onClose={() => setPackageToDelete(null)} width={400}>
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <div style={{ background: '#fef2f2', color: '#ef4444', width: 64, height: 64, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 6h18"></path><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path></svg>
            </div>
            <h3 style={{ fontSize: 18, fontWeight: 800, color: '#0f172a', marginBottom: 8 }}>Are you sure?</h3>
            <p style={{ color: '#64748b', marginBottom: 24, lineHeight: 1.5 }}>
              This will permanently delete <strong>{packageToDelete.name}</strong>. This action cannot be undone.
            </p>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
              <Btn variant="outline" onClick={() => setPackageToDelete(null)}>Cancel</Btn>
              <Btn style={{ background: '#ef4444', color: '#fff' }} onClick={handleDeletePackage}>Delete Package</Btn>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────
// Revisions View
// ─────────────────────────────────────────────
function RevisionsView({ toast, user }) {
  const { projectId, packageId } = useParams();
  const navigate = useNavigate();
  const [revisions, setRevisions] = useState([]);
  const [packageData, setPackageData] = useState(null);
  const [loading, setLoading] = useState(true);

  const [showAdd, setShowAdd] = useState(false);
  const [editingRevision, setEditingRevision] = useState(null);
  const [revisionToDelete, setRevisionToDelete] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState({
    code: '', name: '', description: '', revision_date: new Date().toISOString().split('T')[0], status: 'draft'
  });

  const loadData = async () => {
    setLoading(true);
    try {
      const [revs, pkgs] = await Promise.all([
        api.listPackageRevisions(packageId),
        api.listProjectPackages(projectId)
      ]);
      setRevisions(revs || []);
      const currentPkg = (pkgs || []).find(p => p.id === packageId);
      setPackageData(currentPkg);
    } catch (e) {
      toast?.show?.('Failed to load revisions', 'error');
    }
    setLoading(false);
  };

  useEffect(() => { loadData(); }, [packageId]);

  const handleAddOrUpdate = async () => {
    if (!formData.code || !formData.name || !formData.revision_date) {
      toast?.show?.('Please fill required fields', 'error');
      return;
    }
    setIsSaving(true);
    try {
      if (editingRevision) {
        await api.updatePackageRevision(editingRevision.id, formData);
        toast?.show?.('Revision updated successfully', 'success');
      } else {
        await api.createPackageRevision(packageId, formData);
        toast?.show?.('Revision created successfully', 'success');
      }
      setShowAdd(false);
      setEditingRevision(null);
      setFormData({ code: '', name: '', description: '', revision_date: new Date().toISOString().split('T')[0], status: 'draft' });
      loadData();
    } catch (e) {
      toast?.show?.(e.message || 'Failed to save revision', 'error');
    }
    setIsSaving(false);
  };

  const handleDeleteRevision = async () => {
    try {
      await api.deletePackageRevision(revisionToDelete.id);
      toast?.show?.('Revision deleted successfully', 'success');
      setRevisionToDelete(null);
      loadData();
    } catch (e) {
      toast?.show?.(e.message || 'Failed to delete revision', 'error');
    }
  };

  if (loading) return <RNLLoader title="Loading Revisions..." />;

  return (
    <div className="fade-in">
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
        <PillBackButton onClick={() => navigate(`${BOQ_BASE}/project/${projectId}/packages`)}>Back to Packages</PillBackButton>
        <h2 style={{ margin: 0, fontSize: 24, fontWeight: 800, color: '#0f172a' }}>
          {packageData ? `${packageData.name}` : 'Package Revisions'}
        </h2>
        <div style={{ flex: 1 }} />
        {hasRole(user, BOQ_WRITERS) && <DownloadTemplateBtn projectId={projectId} toast={toast} />}
        {hasRole(user, BOQ_WRITERS) && <Btn variant="secondary" onClick={() => navigate(`${BOQ_BASE}/project/${projectId}/import?package=${packageId}`)}>Import BOQ (Excel)</Btn>}
        <Btn onClick={() => setShowAdd(true)}>New Revision</Btn>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
        {revisions.map(rev => (
          <div
            key={rev.id}
            onClick={() => navigate(`${BOQ_BASE}/project/${projectId}/packages/${packageId}/revisions/${rev.id}/boqs`)}
            style={{
              display: 'flex',
              alignItems: 'center',
              background: '#e2e8f0',
              border: '1px solid black',
              borderRadius: 12,
              padding: '16px 20px',
              cursor: 'pointer',
              gap: 16,
              boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
              transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
              transform: 'translateY(0)',
            }}
            onMouseOver={e => {
              e.currentTarget.style.borderColor = '#0b318bff';
              e.currentTarget.style.background = '#cbd5e1';
              e.currentTarget.style.transform = 'translateY(-4px)';
            }}
            onMouseOut={e => {
              e.currentTarget.style.borderColor = 'black';
              e.currentTarget.style.background = '#e2e8f0';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            <div style={{
              width: 44, height: 44,
              background: '#f1f5f9',
              borderRadius: 10,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: '#64748b'
            }}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline><line x1="16" y1="13" x2="8" y2="13"></line><line x1="16" y1="17" x2="8" y2="17"></line><polyline points="10 9 9 9 8 9"></polyline></svg>
            </div>

            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <span style={{ fontFamily: 'var(--mono)', fontWeight: 800, color: '#0b318bff', fontSize: 13 }}>{rev.code}</span>
                <span style={{ fontWeight: 700, color: '#0f172a', fontSize: 15 }}>{rev.name}</span>
                <Badge color={rev.status === 'approved' ? 'green' : rev.status === 'draft' ? 'gray' : 'amber'}>
                  {rev.status.toUpperCase()}
                </Badge>
                {rev.is_locked && <Badge color="red">LOCKED</Badge>}
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: 16, fontSize: 12, color: '#64748b' }}>
                {rev.revision_date && (
                  <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect><line x1="16" y1="2" x2="16" y2="6"></line><line x1="8" y1="2" x2="8" y2="6"></line><line x1="3" y1="10" x2="21" y2="10"></line></svg>
                    {new Date(rev.revision_date).toLocaleDateString()}
                  </span>
                )}
                {rev.prepared_by && (
                  <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path><circle cx="12" cy="7" r="4"></circle></svg>
                    Prep: {rev.prepared_by}
                  </span>
                )}
                {rev.description && <span style={{ fontStyle: 'italic', color: '#94a3b8' }}>{rev.description}</span>}
              </div>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              <button
                onClick={(e) => { e.stopPropagation(); /* TODO: Duplicate logic */ }}
                style={{
                  background: 'transparent',
                  border: '1.5px solid #cbd5e1',
                  borderRadius: 8,
                  cursor: 'pointer',
                  color: '#64748b',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                  padding: '6px 12px',
                  fontSize: 12,
                  fontWeight: 700,
                  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
                }}
                onMouseOver={e => {
                  e.currentTarget.style.background = '#f8fafc';
                  e.currentTarget.style.borderColor = '#94a3b8';
                  e.currentTarget.style.color = '#334155';
                }}
                onMouseOut={e => {
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.borderColor = '#cbd5e1';
                  e.currentTarget.style.color = '#64748b';
                }}
                title="Duplicate Revision"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path></svg>
                Duplicate
              </button>

              <button
                onClick={(e) => { 
                  e.stopPropagation();
                  setFormData({
                    code: rev.code,
                    name: rev.name,
                    description: rev.description || '',
                    revision_date: rev.revision_date ? new Date(rev.revision_date).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
                    status: rev.status
                  });
                  setEditingRevision(rev);
                  setShowAdd(true);
                }}
                style={{
                  background: 'transparent',
                  border: '1.5px solid #f59e0b',
                  borderRadius: 8,
                  cursor: 'pointer',
                  color: '#d97706',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                  padding: '6px 12px',
                  fontSize: 12,
                  fontWeight: 700,
                  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
                }}
                onMouseOver={e => {
                  e.currentTarget.style.background = '#fffbeb';
                  e.currentTarget.style.borderColor = '#d97706';
                }}
                onMouseOut={e => {
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.borderColor = '#f59e0b';
                }}
                title="Edit Revision"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path></svg>
              </button>

              <button
                onClick={(e) => { e.stopPropagation(); setRevisionToDelete(rev); }}
                style={{
                  background: 'transparent',
                  border: '1.5px solid #fca5a5',
                  borderRadius: 8,
                  cursor: 'pointer',
                  color: '#ef4444',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                  padding: '6px 12px',
                  fontSize: 12,
                  fontWeight: 700,
                  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
                }}
                onMouseOver={e => {
                  e.currentTarget.style.background = '#fef2f2';
                  e.currentTarget.style.borderColor = '#ef4444';
                }}
                onMouseOut={e => {
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.borderColor = '#fca5a5';
                }}
                title="Delete Revision"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path><line x1="10" y1="11" x2="10" y2="17"></line><line x1="14" y1="11" x2="14" y2="17"></line></svg>
              </button>

              <Btn style={{
                background: '#1e3a8a',
                color: '#fff',
                borderRadius: 20,
                padding: '8px 16px',
                fontSize: 13,
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                gap: 6
              }}>
                Select Revision
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="m9 18 6-6-6-6" /></svg>
              </Btn>
            </div>
          </div>
        ))}
        {revisions.length === 0 && (
          <div style={{ padding: 40, textAlign: 'center', color: '#64748b', background: '#fff', borderRadius: 12, border: '1px dashed #cbd5e1' }}>No revisions found for this package</div>
        )}
      </div>

      {showAdd && (
        <Modal title={editingRevision ? "Edit Revision" : "Create New Revision"} onClose={() => { setShowAdd(false); setEditingRevision(null); setFormData({ code: '', name: '', description: '', revision_date: new Date().toISOString().split('T')[0], status: 'draft' }); }} width={600}>
          <FormGrid cols={2}>
            <div>
              <label>Revision Code</label>
              <Input value={formData.code} onChange={v => setFormData({ ...formData, code: v })} placeholder="e.g. REV-01" />
            </div>
            <div>
              <label>Revision Name</label>
              <Input value={formData.name} onChange={v => setFormData({ ...formData, name: v })} placeholder="e.g. Initial Issue" />
            </div>
            <div>
              <label>Revision Date</label>
              <Input type="date" value={formData.revision_date} onChange={v => setFormData({ ...formData, revision_date: v })} />
            </div>
            <div>
              <label>Status</label>
              <Select
                value={formData.status}
                onChange={v => setFormData({ ...formData, status: v })}
                options={[
                  { value: 'draft', label: 'Draft' },
                  { value: 'review', label: 'Under Review' },
                  { value: 'approved', label: 'Approved' }
                ]}
              />
            </div>
            <div style={{ gridColumn: 'span 2' }}>
              <label>Description</label>
              <Textarea value={formData.description} onChange={v => setFormData({ ...formData, description: v })} rows={3} />
            </div>

          </FormGrid>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 24 }}>
            <Btn variant="ghost" onClick={() => { setShowAdd(false); setEditingRevision(null); }}>Cancel</Btn>
            <Btn onClick={handleAddOrUpdate} disabled={isSaving}>{isSaving ? 'Saving...' : (editingRevision ? 'Update Revision' : 'Save Revision')}</Btn>
          </div>
        </Modal>
      )}

      {revisionToDelete && (
        <Modal title="Delete Revision" onClose={() => setRevisionToDelete(null)} width={400}>
          <div style={{ padding: '12px 0' }}>
            <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
              <div style={{ background: '#fef2f2', color: '#ef4444', padding: 12, borderRadius: '50%' }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path><line x1="12" y1="9" x2="12" y2="13"></line><line x1="12" y1="17" x2="12.01" y2="17"></line></svg>
              </div>
              <div style={{ flex: 1 }}>
                <h4 style={{ margin: '0 0 8px 0', fontSize: 16, color: '#0f172a' }}>Are you absolutely sure?</h4>
                <p style={{ margin: 0, fontSize: 14, color: '#64748b', lineHeight: 1.5 }}>
                  This will permanently delete <strong>{revisionToDelete.name}</strong> and all its associated BOQs. This action cannot be undone.
                </p>
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 24 }}>
              <Btn variant="outline" onClick={() => setRevisionToDelete(null)}>Cancel</Btn>
              <Btn style={{ background: '#ef4444', color: '#fff' }} onClick={handleDeleteRevision}>Delete Revision</Btn>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────
// Revision → its BOQ
// ─────────────────────────────────────────────
// A revision opens straight onto its BOQ (buildings are the BOQ's tabs). A revision with no BOQ yet opens
// the create form. Revisions imported before one-BOQ-per-revision can hold several BOQs: a switcher picks one.
function RevisionBOQsView({ toast }) {
  const { projectId, packageId, revisionId } = useParams();
  const navigate = useNavigate();
  const [boqs, setBoqs] = useState([]);
  const [revision, setRevision] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [statusModal, setStatusModal] = useState({ open: false, boq: null, newStatus: null });
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const revisionsUrl = `${BOQ_BASE}/project/${projectId}/packages/${packageId}/revisions`;

  // leaveIfEmpty: once the create form is closed (or the BOQ deleted), an empty revision goes back to the list
  const loadData = async (leaveIfEmpty = false) => {
    setLoading(true);
    try {
      const [list, packageRevs] = await Promise.all([
        api.listRevisionBOQs(revisionId),
        api.listPackageRevisions(packageId),
      ]);
      const rows = Array.isArray(list) ? list : list?.data || [];
      setBoqs(rows);
      setRevision((packageRevs || []).find(r => String(r.id) === String(revisionId)) || null);
      setSelectedId(prev => (rows.some(b => b.id === prev) ? prev : rows[0]?.id || null));
      if (leaveIfEmpty && rows.length === 0) navigate(revisionsUrl);
    } catch (e) {
      toast?.show?.('Failed to load the BOQ of this revision', 'error');
    }
    setLoading(false);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadData(); }, [revisionId, packageId]);

  const handleStatusUpdate = async () => {
    const { boq, newStatus } = statusModal;
    setUpdatingStatus(true);
    try {
      await api.updateBOQStatus(boq.id, newStatus);
      toast?.show?.(`BOQ successfully ${newStatus === 'active' ? 'activated' : 'suspended'}`, 'success');
      setStatusModal({ open: false, boq: null, newStatus: null });
      loadData();
    } catch (e) {
      toast?.show?.(e.message, 'error');
    }
    setUpdatingStatus(false);
  };

  if (loading) return <RNLLoader title="Loading BOQ..." />;

  if (boqs.length === 0) {
    return (
      <div className="fade-in" style={{ marginTop: 0 }}>
        <PillBackButton onClick={() => navigate(revisionsUrl)}>Back to Revisions</PillBackButton>
        <BOQFormPage
          project={{ project_id: projectId }}
          existingBoqData={{ boq_revision_id: revisionId, name: '' }}
          onBack={() => loadData(true)}
          toast={toast}
        />
      </div>
    );
  }

  const boq = boqs.find(b => b.id === selectedId) || boqs[0];
  const isActive = boq.status === 'active';
  const revisionName = revision ? `${revision.code} — ${revision.name}` : 'Revision';

  const barExtra = (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
      {boqs.length > 1 && (
        <select value={boq.id} onChange={e => setSelectedId(e.target.value)}
          title="This revision was imported before one BOQ per revision and holds several BOQs"
          style={{ padding: '6px 10px', border: '1px solid #000', borderRadius: 8, fontSize: 12, fontFamily: 'var(--font)', background: 'var(--bg-surface)', color: 'var(--text-primary)', maxWidth: 280 }}>
          {boqs.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
        </select>
      )}
      <span style={{ color: isActive ? '#14532d' : '#7f1d1d', fontWeight: 800, background: isActive ? '#4ade80' : '#f87171', padding: '4px 10px', borderRadius: 12, fontSize: 11, letterSpacing: '0.05em', border: isActive ? '1px solid #16a34a' : '1px solid #dc2626' }}>{boq.status?.toUpperCase()}</span>
      <Btn size="sm" variant={isActive ? 'danger' : 'success'}
        onClick={() => setStatusModal({ open: true, boq, newStatus: isActive ? 'inactive' : 'active' })}>
        {isActive ? 'Suspend' : 'Activate'}
      </Btn>
    </div>
  );

  return (
    <div className="fade-in" style={{ marginTop: 0 }}>
      <PillBackButton onClick={() => navigate(revisionsUrl)}>Back to Revisions</PillBackButton>
      <BOQItemsTab
        key={boq.id}
        projectId={projectId}
        boqId={boq.id}
        contextLabel={<>Revision: <strong style={{ color: 'var(--text-primary)' }}>{revisionName}</strong></>}
        barExtra={barExtra}
        onDeleted={() => loadData(true)}
        toast={toast}
      />

      {statusModal.open && (
        <Modal title={statusModal.newStatus === 'active' ? 'Activate BOQ' : 'Suspend BOQ'} onClose={() => setStatusModal({ open: false, boq: null, newStatus: null })}>
          <div style={{ padding: '0 0 20px 0', fontSize: 14, color: 'var(--text-primary)' }}>
            Are you sure you want to {statusModal.newStatus === 'active' ? 'activate' : 'suspend'} the BOQ <strong>{statusModal.boq?.name}</strong>?
            {statusModal.newStatus === 'inactive' && <div style={{ marginTop: 8, color: 'var(--text-muted)' }}>Suspending this BOQ will hide its line items from all dropdowns.</div>}
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <Btn variant="ghost" onClick={() => setStatusModal({ open: false, boq: null, newStatus: null })}>Cancel</Btn>
            <Btn
              variant={statusModal.newStatus === 'active' ? 'primary' : 'danger'}
              loading={updatingStatus}
              onClick={handleStatusUpdate}
            >
              {statusModal.newStatus === 'active' ? 'Activate' : 'Suspend'}
            </Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}

// Old links to the retired Units step land on the revision's BOQs.
function UnitsRedirect() {
  const { projectId, packageId, revisionId } = useParams();
  return <Navigate replace to={`${BOQ_BASE}/project/${projectId}/packages/${packageId}/revisions/${revisionId}/boqs`} />;
}

function BudgetMaster({ toast, user }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [projects, setProjects] = useState([]);
  const [loadingInitial, setLoadingInitial] = useState(true);

  // Extract projectId from URL if present
  const match = location.pathname.match(/\/project\/([^\/]+)/);
  const selectedProjectId = match ? match[1] : '';

  useEffect(() => {
    api.listProjects().then(res => {
      setProjects(Array.isArray(res) ? res : (res?.data || res?.items || []));
      setLoadingInitial(false);
    }).catch(e => {
      toast?.show?.('Failed to load projects', 'error');
      setLoadingInitial(false);
    });
  }, [toast]);

  if (loadingInitial) return <RNLLoader title="Loading Master Data..." subtitle="Please wait" />;

  const handleProjectSelect = (id) => {
    if (id) {
      navigate(`${BOQ_BASE}/project/${id}`);
    } else {
      navigate(BOQ_BASE);
    }
  };

  return (
    <div className="fade-in" style={{ padding: '10px 0', maxWidth: '100%', margin: '0 auto', width: '100%' }}>
      <div style={{ background: '#ffffff', border: '3px solid black', borderRadius: 22, padding: 18, boxShadow: 'var(--shadow-lg)' }}>
        <div style={{ borderRadius: 12, overflow: 'hidden', background: '#ffffff', border: '1px solid black' }}>
          {/* PROJECT SELECTION BAR & TITLE */}
          <div style={{ background: '#0b318bff', padding: '18px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <h2 style={{ margin: 0, fontSize: 19, fontWeight: 800, color: '#ffffff', letterSpacing: '0.02em' }}>
              Project Bill of Quantities Hub
            </h2>

            <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
              <div style={{ color: '#fff', fontSize: 14, fontWeight: 800, whiteSpace: 'nowrap' }}>SELECT PROJECT:</div>
              <div style={{ width: 280 }}>
                <Select
                  value={selectedProjectId}
                  onChange={handleProjectSelect}
                  options={[{ value: '', label: '— Select Project Context —' }, ...projects.map(p => ({ value: p.project_id || p.id, label: p.project_name }))]}
                  style={{ background: '#fff', border: 'none', borderRadius: 8, height: 38 }}
                />
              </div>
            </div>
          </div>

          <div style={{ padding: '24px' }}>

            <style>
              {`
              .division-card {
                background: #b7c5e2ff;
                border: 2px solid black;
                border-radius: 16px;
                padding: 24px;
                display: flex;
                flex-direction: column;
                gap: 16px;
                cursor: pointer;
                position: relative;
                overflow: hidden;
                box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03);
                transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
              }
              .division-card::before {
                content: '';
                position: absolute;
                top: 0; left: 0; right: 0;
                height: 4px;
                background: linear-gradient(90deg, #0b318bff, #3b82f6);
                opacity: 0;
                transition: opacity 0.3s ease;
              }
              .division-card:hover {
                transform: translateY(-8px);
                border-color: #0b318bff;
                box-shadow: 0 20px 25px -5px rgba(11, 49, 139, 0.1), 0 10px 10px -5px rgba(11, 49, 139, 0.04);
              }
              .division-card:hover::before {
                opacity: 1;
              }
              .division-card .icon-wrapper {
                width: 48px; height: 48px;
                border-radius: 12px;
                background: #f1f5f9;
                color: #0b318bff;
                display: flex; align-items: center; justify-content: center;
                transition: all 0.3s ease;
              }
              .division-card:hover .icon-wrapper {
                background: #0b318bff;
                color: #ffffff;
                transform: scale(1.15) rotate(12deg);
              }
              .division-card .arrow-icon {
                transform: translateX(0);
                transition: transform 0.3s ease;
              }
              .division-card:hover .arrow-icon {
                transform: translateX(4px);
              }
            `}
            </style>

            <Routes>
              <Route path="/" element={
                <div style={{ padding: 60, textAlign: 'center', color: 'var(--text-muted)' }}>
                  <div style={{ fontSize: 40, marginBottom: 16 }}>🏢</div>
                  <h3 style={{ fontSize: 18, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 8 }}>No Project Selected</h3>
                  <p>Please select a project from the dropdown above to continue with the entry.</p>
                </div>
              } />
              <Route path="/project/:projectId" element={<ProjectSummaryView projects={projects} />} />
              <Route path="/project/:projectId/packages" element={<PackagesView toast={toast} projects={projects} user={user} />} />
              <Route path="/project/:projectId/import" element={<BOQImportView toast={toast} projects={projects} />} />
              <Route path="/project/:projectId/packages/:packageId/revisions" element={<RevisionsView toast={toast} user={user} />} />
              <Route path="/project/:projectId/packages/:packageId/revisions/:revisionId/boqs" element={<RevisionBOQsView toast={toast} />} />
              <Route path="/project/:projectId/packages/:packageId/revisions/:revisionId/units" element={<UnitsRedirect />} />
            </Routes>
          </div>
        </div>
      </div>
    </div>
  );
}

// Old addresses (/boq-hub/project/…) from before the tabs land in Budget Master.
function LegacyBudgetMasterRedirect() {
  const location = useLocation();
  return <Navigate replace to={location.pathname.replace(/^\/boq-hub/, BOQ_BASE) + location.search} />;
}

// BOQ Hub: a card per sub-module; each card opens its own screen.
function BOQHubCards() {
  const navigate = useNavigate();
  return (
    <div className="fade-in">
      <p style={{ fontSize: 14, color: 'var(--text-secondary)', margin: '0 0 22px' }}>
        Select a BOQ Hub module below.
      </p>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 18 }}>
        {HUB_CARDS.map(card => (
          <ModuleCard key={card.id} item={card} badge={card.comingSoon ? 'Coming Soon' : null}
            onOpen={() => navigate(`/boq-hub/${card.id}`)} />
        ))}
      </div>
    </div>
  );
}

export default function BOQHubPage({ toast, user }) {
  return (
    <Routes>
      <Route index element={<BOQHubCards />} />
      <Route path="budget-master/*" element={<BudgetMaster toast={toast} user={user} />} />
      <Route path="material-mappings" element={<MaterialMappingPage toast={toast} user={user} />} />
      <Route path="material-mappings/map/:boqItemId" element={<MaterialMappingPage toast={toast} user={user} />} />
      <Route path="material-mappings/:tab" element={<MaterialMappingPage toast={toast} user={user} />} />
      <Route path="code-generator" element={<CodeGeneratorPage toast={toast} user={user} />} />
      <Route path="code-generator/:tab" element={<CodeGeneratorPage toast={toast} user={user} />} />
      <Route path="project/*" element={<LegacyBudgetMasterRedirect />} />
      <Route path="*" element={<Navigate replace to="/boq-hub" />} />
    </Routes>
  );
}
